import type { FC } from "react";
import { Modal, Platform, Pressable, View } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import "../../../theme";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import type { CountryOption } from "../phone-number";
import { CountryList } from "./CountryList";
import { CountrySearchField } from "./CountrySearchField";

interface CountryPickerSheetProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly query: string;
  readonly onQueryChange: (text: string) => void;
  readonly options: readonly CountryOption[];
  readonly selected: CountryOption["country"];
  readonly onSelect: (option: CountryOption) => void;
}

/**
 * Native country picker: a full-height sheet rather than an anchored dropdown.
 * A dropdown under a field halfway down a form leaves a few rows visible once
 * the keyboard is up; the sheet keeps search and the list above the keyboard,
 * and Android's back button closes it via `onRequestClose`.
 */
const CountryPickerSheet: FC<CountryPickerSheetProps> = ({
  isOpen,
  onClose,
  title,
  query,
  onQueryChange,
  options,
  selected,
  onSelect,
}) => {
  const { theme } = useUnistyles();
  return (
    <Modal visible={isOpen} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <View style={styles.sheet}>
        <View style={styles.header}>
          <Text textStyle="md/semibold" color="text">
            {title}
          </Text>
          <Pressable onPress={onClose} hitSlop={12} accessibilityRole="button" accessibilityLabel="Close">
            <Icon name="close" size={22} color={theme.colors.text} />
          </Pressable>
        </View>
        <View style={styles.search}>
          {/* No autofocus: the keyboard would cover most of the list before the user has chosen to search. */}
          <CountrySearchField value={query} onChangeText={onQueryChange} autoFocus={false} />
        </View>
        <View style={styles.list}>
          <CountryList
            options={options}
            selected={selected}
            highlightedIndex={-1}
            onSelect={onSelect}
            onHighlight={() => undefined}
          />
        </View>
      </View>
    </Modal>
  );
};

CountryPickerSheet.displayName = "CountryPickerSheet";

export { CountryPickerSheet };

const styles = StyleSheet.create((theme, rt) => ({
  sheet: {
    flex: 1,
    backgroundColor: theme.colors.surface,
    // Edge-to-edge Android draws modals under both system bars; an iOS page
    // sheet already sits below the status bar.
    paddingTop: Platform.OS === "android" ? rt.insets.top : 0,
    paddingBottom: rt.insets.bottom,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: theme.spacing["3xl"],
    paddingTop: theme.spacing["3xl"],
    paddingBottom: theme.spacing.xl,
  },
  search: {
    paddingHorizontal: theme.spacing["3xl"],
    paddingBottom: theme.spacing.md,
  },
  list: {
    flex: 1,
  },
}));
